import { ListItem } from "../model/datasource/list";
import { AttemptState } from "../model/datasource/state";
import { ItemId, Serializer } from "../model/datasource";

export default class Current<I extends ItemId> {
	index: number;
	answer_visible: boolean;
	items: ListItem<I>[];
	active: number[];
	order: number[];
	ordered_active: number[];
	serializer: Serializer<I>;

	constructor() {
		this.index = 0;
		this.items = [];
		this.active = [];
		this.order = [];
		this.ordered_active = [];
		this.serializer = new Serializer();
	}

	init(items: ListItem<I>[], state: AttemptState["current"] | undefined, randomize: boolean, map: Record<string, number>, serializer: Serializer<I>): void {
		const keys = Array.from(items.keys());
		this.items = items;
		this.index = state?.id ? map?.[state.id] ?? 0 : 0;
		this.active = keys;
		this.order = Object.entries(randomize ? state?.order ?? {} : {}).reduce((result: number[], [id, order]) => {
			result[order] = map[id];
			return result;
		}, keys);
		if (randomize) {
			this.order = this.order
				.filter((_, i) => Object.values(state?.order ?? {}).includes(i))
				.filter(v => v !== undefined);
			Object.entries(map)
				.filter(([id]) => !Object.keys(state?.order ?? {}).includes(id))
				.forEach(([, order]) => {
					this.order.splice(Math.floor(Math.random() * (this.order.length + 1)), 0, order);
				});
		}
		this.ordered_active = keys;
		this.serializer = serializer;
		this.update();
	}

	get(): number { return this.index; }

	getOrder(): number[] { return this.order; }

	getPosition(): number {
		const i = this.ordered_active.findIndex(index => index === this.index);
		return i >= 0 ? i : 0;
	}

	getVisible(): number[] { return this.ordered_active; }

	getAdjacent(offset: number): number {
		if (offset === 0) { return this.index; }
		const i = this.ordered_active.findIndex(a => a === this.index);
		if (i >= 0) {
			if (
				(offset < 0 && i + offset >= 0) ||
				(offset > 0 && i + offset < this.ordered_active.length)
			) {
				return this.ordered_active[i + offset];
			}
			return this.index;
		} else {
			return this.ordered_active.at(0) ?? this.index;
		}
	}

	last(): number { return this.items.length - 1; }

	set(index: number): number {
		this.index = index;
		return this.index;
	}

	jump(item: I): number {
		const found = this.items.findIndex(i =>
			this.compare(i.id, item)
		);
		this.index = found < 0 ? this.index : found;
		return found;
	}

	next(): number {
		this.index = this.getAdjacent(1);
		return this.index;
	}

	previous(): number {
		this.index = this.getAdjacent(-1);
		return this.index;
	}

	setActive(filtered: ListItem<I>[]): number[] {
		this.active = this.items
			.map(item =>
				filtered.some(filter => this.compare(filter.id, item.id))
			).map((match, index) => match ? index : -1)
			.filter(index => index >= 0);
		this.update();
		return this.active;
	}

	getAnswerVisible(): boolean {
		return this.answer_visible;
	}

	setAnswerVisible(visible: boolean): boolean {
		this.answer_visible = visible;
		return this.answer_visible;
	}

	randomize(randomize: boolean): number[] {
		if (randomize) {
			Current.shuffle(this.order);
		} else {
			this.order.sort((a, b) => a - b);
		}
		this.update();
		this.index = this.ordered_active.at(0) ?? this.index;
		return this.order;
	}

	update(): number[] {
		this.ordered_active = Array.from(this.order.values())
			.filter(i => this.active.includes(i));
		return this.ordered_active;
	}

	serialize(map: string[]): AttemptState["current"] {
		return {
			id: map[this.index],
			order: this.order.reduce((result: Record<string, number>, index, order) => {
				return ({
					...result,
					[map[index]]: order,
				});
			}, {}),
		};
	}

	compare(a: I, b: I): boolean {
		return this.serializer.toString(a) === this.serializer.toString(b);
	}

	// Relocated to static class functions for testability. ---

	/**
	 * The `shuffle()` method shuffles the elements of an array in place and
	 * returns the shuffled array. This method adheres to the same conditions
	 * as `Array.sort()` at the time of writing. Note that, like `Array.sort()`,
	 * the array is shuffled in place, and no copy is made. __Unlike__
	 * `Array.sort()`, `shuffle()` does not provide a stable "sort".
	 *
	 * @param array An array to shuffle.
	 * @return The in place shuffled array.
	 */
	static shuffle(array: number[]): number[] {
		for (let i = array.length - 1; i >= 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			const temp = array[i];
			array[i] = array[j];
			array[j] = temp;
		}
		return array;
	}
}
